<template>
  <v-card class="pa-4 elevation-1">
    <div class="d-flex align-center justify-space-between flex-wrap mb-3">
      <v-card-title class="text-subtitle-1 font-weight-bold pa-0">
        {{ $t('surveys.nps.comments_title') }} ({{ total }})
      </v-card-title>
      <div class="d-flex align-center">
        <v-checkbox
          v-model="negativeOnly"
          :label="$t('surveys.export.negative_only')"
          dense
          hide-details
          class="mr-4 mt-0 pt-0"
        />
        <v-btn
          text
          small
          color="deep-purple lighten-1"
          :loading="exporting"
          :disabled="exporting"
          @click="exportAllComments"
        >
          <v-icon left small>mdi-download</v-icon>
          {{ $t('surveys.export.button') }}
        </v-btn>
        <vue-json-to-csv
          ref="exportTrigger"
          :json-data="commentsToExport"
          :labels="exportLabels"
          :csv-title="exportFilename"
          separator=";"
          class="d-none"
        />
      </div>
    </div>

    <v-data-table
      :headers="headers"
      :items="items"
      :loading="loading"
      :server-items-length="total"
      :options.sync="options"
      :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
      :no-data-text="emptyText"
      class="elevation-0"
    >
      <template v-slot:item.user="{ item }">
        {{ [item.userName, item.userLastname].filter(Boolean).join(' ') || '—' }}
      </template>
      <template v-slot:item.npsScore="{ item }">
        <v-chip :color="scoreColor(item.npsScore)" x-small dark>{{ item.npsScore }}</v-chip>
      </template>
      <template v-slot:item.createdAt="{ item }">{{ formatDate(item.createdAt) }}</template>
    </v-data-table>
  </v-card>
</template>

<script>
import VueJsonToCsv from 'vue-json-to-csv';
import surveysService from '@/services/surveys.service';
import { npsScoreColor } from '@/utils/nps';
import snackbarMixin from '@/mixins/snackbar';

export default {
  name: 'NpsComments',
  components: { VueJsonToCsv },
  mixins: [snackbarMixin],
  props: {
    audience: { type: String, required: true },
    campaignId: { type: Number, required: true },
    campaignName: { type: String, default: '' },
    total: { type: Number, default: 0 },
    country: { type: String, default: null },
  },
  data: () => ({
    items: [],
    loading: false,
    exporting: false,
    negativeOnly: false,
    commentsToExport: [],
    options: { page: 1, itemsPerPage: 25 },
    // Última combinación de parámetros efectivamente pedida al backend. Evita el
    // doble fetch al montar: v-data-table normaliza `options` (agrega sortBy,
    // sortDesc, etc.) apenas se crea y reasigna la prop vía .sync, lo cual
    // dispara de nuevo el watcher de `options` con los mismos page/itemsPerPage.
    lastFetchKey: null,
  }),
  computed: {
    headers() {
      return [
        {
          text: this.$t('surveys.nps.col_score'), value: 'npsScore', align: 'center', sortable: false,
        },
        { text: this.$t('surveys.nps.col_comment'), value: 'comment', sortable: false },
        { text: this.$t('surveys.nps.col_user'), value: 'user', sortable: false },
        { text: this.$t('surveys.nps.col_school'), value: 'schoolName', sortable: false },
        { text: this.$t('surveys.nps.col_country'), value: 'country', sortable: false },
        { text: this.$t('surveys.nps.col_date'), value: 'createdAt', sortable: false },
      ];
    },
    exportLabels() {
      return {
        npsScore: { title: this.$t('surveys.nps.col_score') },
        comment: { title: this.$t('surveys.nps.col_comment') },
        user: { title: this.$t('surveys.nps.col_user') },
        schoolName: { title: this.$t('surveys.nps.col_school') },
        country: { title: this.$t('surveys.nps.col_country') },
        createdAt: { title: this.$t('surveys.nps.col_date') },
      };
    },
    exportFilename() {
      return `comentarios_${this.campaignName}_${new Date().toISOString().slice(0, 10)}`;
    },
    emptyText() {
      return this.negativeOnly
        ? this.$t('surveys.nps.comments_empty_negative')
        : this.$t('surveys.nps.comments_empty');
    },
  },
  watch: {
    options: {
      deep: true,
      handler() {
        this.loadComments();
      },
    },
    negativeOnly() {
      this.options = { ...this.options, page: 1 };
    },
    // Salvaguarda defensiva: hoy Surveys.vue destruye y recrea todo el
    // subárbol NPS (v-if/v-else-if) cada vez que cambia la campaña
    // seleccionada, así que el prop `campaignId` de una instancia viva de
    // este componente nunca cambia y este watcher no se ejecuta en
    // producción. Se deja por si un futuro refactor de Surveys.vue llega a
    // reutilizar la instancia en lugar de recrearla.
    campaignId() {
      this.options = { ...this.options, page: 1 };
    },
    country() {
      this.options = { ...this.options, page: 1 };
      this.loadComments();
    },
  },
  mounted() {
    // Se llama explícitamente aquí (en vez de depender de un watcher con
    // `immediate: true`) para no atarse al detalle interno de v-data-table
    // que reasigna `options` apenas se crea. El guard de `lastFetchKey` en
    // loadComments() evita que ese eco de Vuetify dispare un segundo fetch.
    this.loadComments();
  },
  methods: {
    async loadComments() {
      const fetchKey = `${this.campaignId}:${this.options.page}:${this.options.itemsPerPage}:${this.negativeOnly}:${this.country}`;
      // Ya se pidió esta misma combinación de parámetros (típicamente el eco de
      // v-data-table al normalizar `options` apenas se monta): no la repitas.
      if (fetchKey === this.lastFetchKey) return;
      // Se marca antes de esperar la respuesta para que una segunda invocación
      // disparada mientras esta sigue en vuelo ya vea la clave actualizada.
      this.lastFetchKey = fetchKey;

      this.loading = true;
      try {
        const result = await surveysService.getNpsComments(
          this.audience,
          this.campaignId,
          this.options.page,
          this.options.itemsPerPage,
          { negativeOnly: this.negativeOnly, country: this.country },
        );
        this.items = result.items;
      } catch (_error) {
        this.items = [];
        this.snackbarError(this.$t('general.error_msg'));
      } finally {
        this.loading = false;
      }
    },
    // A diferencia de los otros botones de exportar (que usan datos ya
    // cargados), este pide TODOS los comentarios al backend antes de poder
    // exportar. `vue-json-to-csv` solo dispara la descarga en el click de su
    // propio elemento — por eso el `ref` oculto: se llenan los datos primero
    // y recién ahí se dispara el click de forma programática.
    async exportAllComments() {
      if (this.exporting) return;
      this.exporting = true;
      try {
        const items = await surveysService.exportComments(
          this.audience,
          this.campaignId,
          { negativeOnly: this.negativeOnly, country: this.country },
        );
        // vue-json-to-csv tira un throw síncrono si json-data está vacío
        // (JsonToCsv.vue, verificado en node_modules) — se corta acá antes,
        // con un mensaje claro en vez de un error sin manejar.
        if (!items.length) {
          this.snackbarError(this.emptyText);
          return;
        }
        // Mismo tratamiento que en pantalla: nombre combinado con el fallback
        // '—' (userName/userLastname pueden venir null de nps_responses) y
        // fecha recortada igual que formatDate(). schoolName/country también
        // pueden venir null de la misma tabla — se los deja en '' en vez del
        // literal "null" que produciría vue-json-to-csv con un valor nulo.
        // vue-json-to-csv arma cada celda como '"' + valor + '"' sin escapar
        // comillas internas, así que se duplican acá para no romper el
        // delimitado de columnas en comment/user/schoolName (texto libre).
        const escape = (value) => String(value || '').replace(/"/g, '""');
        this.commentsToExport = items.map((item) => ({
          npsScore: item.npsScore,
          comment: escape(item.comment),
          user: escape([item.userName, item.userLastname].filter(Boolean).join(' ')) || '—',
          schoolName: escape(item.schoolName),
          country: item.country || '',
          createdAt: this.formatDate(item.createdAt),
        }));
        await this.$nextTick();
        this.$refs.exportTrigger.$el.click();
      } catch (_error) {
        this.snackbarError(this.$t('general.error_msg'));
      } finally {
        this.exporting = false;
      }
    },
    scoreColor: npsScoreColor,
    formatDate(value) {
      return value ? String(value).slice(0, 10) : '—';
    },
  },
};
</script>
